import { useRef, useState } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Outlet, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CustomCellRendererProps } from 'ag-grid-react';
import { ColDef, GridApi, SelectionChangedEvent } from 'ag-grid-community';
import { BlockApiModel, BlockRelationship, WithTemplates } from '../../../types/models/block.model';
import { InfniteScrollingGrid } from '../components/AgGrid/InfiniteScrollingGrid';
import { searchBlocks } from '../api/blocks.api';
import { useConfirm } from '../contexts/confirm.context';
import { useDeleteBlock } from '../hooks/api/use-blocks.hook';
import { InfiniteScrollingGridSelection } from '../components/AgGrid/InfiniteScrollingGridSelection';
import { EditBlocks } from '../components/Blocks/EditBlocks';
import { InfiniteScrollingGridSelectAllHeader } from '../components/AgGrid/InfiniteScrollingGridSelectAllHeader';

export const Blocks = () => {
  const gridApi = useRef<GridApi>();
  const [selectedRows, setSelectedRows] = useState<BlockApiModel[]>([]);
  const [isEditBlocks, setIsEditBlocks] = useState(false);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const navigate = useNavigate();
  const [showConfirm] = useConfirm();
  const [deleteBlock, { loading: deletingBlock, error: deleteBlockError }] = useDeleteBlock();

  const columns: ColDef<BlockApiModel & WithTemplates>[] = [
    {
      field: 'name',
      valueGetter: ({ data }) => data?.name,
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'templates',
      headerName: 'Template(s)',
      valueGetter: ({ data }) => data?.templates?.map(({ name }) => name).join(', ') ?? '',
    },
    {
      headerName: 'Actions',
      minWidth: 200,
      maxWidth: 200,
      cellRenderer: ({ api, node }: CustomCellRendererProps<BlockApiModel>) => (
        <>
          <IconButton onClick={() => navigate(`edit/${node.id}`)}>
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              showConfirm({
                onConfirm: async () => {
                  if (node.id) {
                    await deleteBlock({ blockId: +node.id });
                    api.refreshInfiniteCache();
                    window.dispatchEvent(new CustomEvent('rowsDeleted', { detail: { nodes: [node] } }));
                  }
                },
                title: 'Delete Block',
                message: `Are you sure you want to delete block "${node.data?.name}"?`,
                error: deleteBlockError?.response?.data?.message,
              });
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const getBlocks: Parameters<typeof InfniteScrollingGrid<BlockApiModel>>[0]['onGetRows'] = async ({
    cursor,
    limit,
    filterQuery,
    sortQuery,
  }) => {
    const { data } = await searchBlocks({
      name: filterQuery?.name,
      include: [BlockRelationship.TEMPLATES],
      cursor,
      limit,
      sort: sortQuery,
    });

    return data;
  };

  const handleSelectionChange = ({ api }: Pick<SelectionChangedEvent, 'api'>) => {
    setSelectedRows(api.getSelectedRows());
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Outlet />
      <Helmet title="CAD Blocks" />
      <Box display="flex" flexDirection="column" sx={{ height: '100%', p: '0 12px' }}>
        <Box display="flex" sx={{ m: '10px 0' }} justifyContent="space-between">
          <Typography variant="h5">CAD Blocks</Typography>
          <Box display="flex" gap={1.5}>
            <Button color="primary" variant="contained" startIcon={<EditIcon />} onClick={() => setIsEditBlocks(true)}>
              Edit Blocks
            </Button>
            <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`add`)}>
              Add Blocks
            </Button>
          </Box>
        </Box>

        <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
          <InfiniteScrollingGridSelection
            onClearSelection={() => gridApi.current?.deselectAll()}
            selectedRowCount={selectedRows.length}
          />
          <InfniteScrollingGrid
            onGridReady={({ api }) => {
              gridApi.current = api;
            }}
            loading={deletingBlock}
            columnDefs={columns}
            selectionColumnDef={{
              pinned: 'left',
              headerComponent: InfiniteScrollingGridSelectAllHeader,
              headerComponentParams: { onSelectAll: (value: boolean) => setIsSelectAll(value), isSelectAll },
            }}
            rowSelection={{
              mode: 'multiRow',
              selectAll: 'filtered',
            }}
            onGetRows={getBlocks}
            onSelectionChanged={handleSelectionChange}
            onModelUpdated={handleSelectionChange}
          />
        </Box>
      </Box>
      <EditBlocks
        isSelectAll={isSelectAll}
        isOpen={isEditBlocks}
        blocks={selectedRows}
        onClose={() => {
          gridApi.current?.refreshInfiniteCache();
          setIsEditBlocks(false);
        }}
      />
    </Box>
  );
};
